import Constants from '../../Constants';
import BoundingBox from '../BoundingBox';
import VirtualDomElement from '../VirtualDomElement';
import StaticImageElement from './StaticImageElement';

const OFFSET_FACTOR_TEXT = 1.2;

const DEFAULT_PADDING = 5;

export default class StaticTextElement extends VirtualDomElement {
  private hasMeasuredText = false;
  private imageElement: StaticImageElement;

  constructor({
    text,
    fontSize,
    x,
    y,
    color = '#000000',
    bold = true,
    blur = false,
    imageRegistry,
    url,
    onRequestDraw,
    padding = DEFAULT_PADDING,
    showPanel = false,
    panelColor = '#fff',
    strokeWidth = 1,
  }) {
    super(onRequestDraw);

    this.set('opacity', 0.0);
    this.set('fontSize', fontSize);
    this.set('text', text);
    this.set('x', x);
    this.set('y', y);
    this.set('width', (fontSize * text.length) / 2); //vague approximation of text space (could optimize in the future) - is overriden after first draw
    this.set('height', fontSize * 1.1);
    this.set('fontSize', fontSize);
    this.set('color', color);
    this.set('bold', bold);
    this.set('blur', blur);
    this.set('padding', padding);
    this.set('showPanel', showPanel);
    this.set('panelColor', panelColor);
    this.set('strokeWidth', strokeWidth);

    if (url) {
      this.imageElement = new StaticImageElement({
        x: x,
        y: y,
        url: url,
        maxWidth: Constants.general.defaultPictureWidth,
        minRatio: Constants.documentPoint.minRatioDocumentPicture,
        maxRatio: Constants.documentPoint.maxRatioDocumentPicture,
        imageRegistry: imageRegistry,
        onRequestDraw: onRequestDraw,
      });

      imageRegistry.register(url, this.imageElement);
      this.updateDimensionsFromImageElement();
    }

    this.hasMeasuredText = false;
  }

  override getBoundingBox(transform): BoundingBox {
    const [x, y] = transform.apply([this.get('x'), this.get('y')]);
    const width = this.get('width');
    const height = this.get('height');

    return new BoundingBox(
      x - this.get('padding'),
      x + width + this.get('padding'),
      y - this.get('padding'),
      y + height + this.get('padding'),
    );
  }

  setDimensionsFromMeasuredText(ctx) {
    //if no image is available => text indicates width of the component
    const measuredText = ctx.measureText(this.get('text'));
    this.set('width', measuredText.width);

    this.hasMeasuredText = true;
  }

  updateDimensionsFromImageElement() {
    this.set(
      'height',
      this.imageElement.get('height') +
        this.get('fontSize') * OFFSET_FACTOR_TEXT,
    );
    this.set('width', this.imageElement.get('width'));
  }

  onLoad(image) {
    if (this.imageElement) {
      this.imageElement.onLoad(image);
      this.updateDimensionsFromImageElement();
    }
  }

  onError() {
    //url could not be loaded
  }

  drawPlaceHolder(ctx, transform) {
    ctx.globalAlpha = 1;
    const [x, y] = transform.apply([this.get('x'), this.get('y')]);
    ctx.fillStyle = this.get('color');
    ctx.fillRect(x, y, 3, 3);
  }

  draw(ctx, transform) {
    ctx.globalAlpha = this.get('opacity');
    ctx.beginPath();

    ctx.font = `${this.get('bold') ? 'bold ' : ''}${this.get('fontSize')}px ${Constants.general.fontFamily}`;
    ctx.textAlign = 'left';

    if (!this.hasMeasuredText && !this.imageElement) {
      this.setDimensionsFromMeasuredText(ctx);
    }

    const [x, y] = transform.apply([this.get('x'), this.get('y')]);

    if (this.get('showPanel')) {
      ctx.lineWidth = this.get('strokeWidth');
      const padding = this.get('padding');
      ctx.fillStyle = this.get('panelColor');
      ctx.fillRect(
        x - padding,
        y - padding,
        this.get('width') + 2 * padding,
        this.get('height') + 2 * padding,
      );
      ctx.strokeStyle = '#ccc';
      ctx.rect(
        x - padding,
        y - padding,
        this.get('width') + 2 * padding,
        this.get('height') + 2 * padding,
      );
      ctx.stroke();
      ctx.beginPath();
    }

    if (this.get('blur')) {
      ctx.shadowBlur = 1;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.shadowColor = this.get('color');
    }

    if (this.imageElement) {
      ctx.translate(0, this.get('fontSize') * OFFSET_FACTOR_TEXT);
      this.imageElement.draw(ctx, transform);
      ctx.setTransform(1, 0, 0, 1, 0, 0); //reset transform
    }

    ctx.fillStyle = this.get('color');
    ctx.textAlign = 'center';
    ctx.fillText(
      this.get('text'),
      x + this.get('width') / 2,
      y + this.get('fontSize'),
    );
    ctx.globalAlpha = 1;
  }
}
